import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  SettingOutlined,
  BellOutlined,
  LogoutOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import ReusableModal from "src/components/reusable/ConfirmationModal";
import { clearLocalStorageExcept } from "src/utils/HelperFunctions";
import { useUser } from "src/contexts/UserContext";
import { useTranslation } from "react-i18next";

interface DropDownProfileProps {
  userType: "admin" | "user";
}

const capitalizeWords = (str: string) =>
  str
    .split(" ")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

const DropDownProfile: React.FC<DropDownProfileProps> = ({ userType }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { user } = useUser();

  // 🆕 Prefer first_name + last_name, else fallback to user_name
  const displayName =
    user?.first_name && user?.last_name
      ? capitalizeWords(`${user.first_name} ${user.last_name}`)
      : user?.user_name
      ? capitalizeWords(user.user_name) // 🔑 apply here too
      : t("profileDropdown.defaultUser");

  const handleLogout = () => {
    if (userType === "admin") {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_name");
    } else {
      clearLocalStorageExcept(["admin_token", "admin_name"]);
    }

    sessionStorage.clear();
    const path = userType === "admin" ? "/admin-portal/log-in" : "/log-in";
    navigate(path);
  };

  const handleCancel = () => {
    setModalOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(prev => !prev);
  };

  const navigateTo = (path: string, menuKey?: number) => {
    navigate(path, menuKey ? { state: { menuKey } } : undefined);
    setIsDropdownOpen(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div style={{ position: "relative" }} ref={dropdownRef}>
      <Avatar
        size={35}
        src={user?.profile_image_url} // ✅ live updates from context
        icon={
          !user?.profile_image_url && (
            <UserOutlined style={{ fontSize: "18px", marginTop: "5px" }} />
          )
        }
        onClick={() => setIsDropdownOpen(prev => !prev)}
        style={{ cursor: "pointer", backgroundColor: "#2c68ad", color: "#fff" }}
      />

      {isDropdownOpen && (
        <ul
          style={{
            listStyle: "none",
            margin: 0,
            padding: 0,
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: "4px",
            position: "absolute",
            insetInlineEnd: 0,
            zIndex: 999,
            minWidth: "180px",
            marginTop: "0px",
          }}
        >
          {/* Common Username Display */}
          <li style={{ padding: "10px 12px", fontWeight: "bold" }}>
            {displayName}
          </li>

          {/* ✅ Admin Dropdown Items */}
          {userType === "admin" ? (
            <>
              <li
                onClick={() => navigate("/admin-portal/settings")}
                style={{ padding: "8px 8px", cursor: "pointer" }}
              >
                <SettingOutlined style={{ marginInlineEnd: "8px" }} />
                {t("profileDropdown.adminSettings")}
              </li>
            </>
          ) : (
            <>
              {/* ✅ User Dropdown Items */}
              <li
                onClick={() =>
                  navigateTo("/account/my-account/account-settings", 51)
                }
                style={{ padding: "8px 8px", cursor: "pointer" }}
              >
                <SettingOutlined style={{ marginInlineEnd: "8px" }} />
                {t("profileDropdown.accountSettings")}
              </li>
              <li
                onClick={() =>
                  navigateTo("/account/my-account/notification-preferences", 53)
                }
                style={{ padding: "8px", cursor: "pointer" }}
              >
                <BellOutlined style={{ marginInlineEnd: "8px" }} />
                {t("profileDropdown.notificationPreferences")}
              </li>
            </>
          )}

          <hr style={{ margin: "8px 0", borderColor: "#eee" }} />

          <li
            onClick={() => setModalOpen(true)}
            style={{
              padding: "8px 12px",
              cursor: "pointer",
              color: "red",
              display: "flex",
              alignItems: "center",
              marginBottom: "10px",
            }}
          >
            <LogoutOutlined style={{ marginInlineEnd: "8px", color: "red" }} />
            {t("profileDropdown.logout")}
          </li>
        </ul>
      )}

      <ReusableModal
        modalOpen={modalOpen}
        onCancel={handleCancel}
        onConfirm={handleLogout}
        title={t("profileDropdown.confirmLogoutTitle")}
        description={t("profileDropdown.confirmLogoutDescription")}
        icon="/Icons/Logout Icon.svg"
        confirmText={t("profileDropdown.confirmLogout")}
        cancelText={t("profileDropdown.cancel")}
      />
    </div>
  );
};

export default DropDownProfile;
